import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { Markdown } from 'tiptap-markdown';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { KnowledgeBaseDocument, knowledgeBaseDocumentApi, knowledgeBaseAIApi } from '../../api/knowledge-base';
import { XIcon, SparklesIcon } from 'lucide-react';
import { TagInput } from './TagInput';
import { VisaTypeSelector } from './VisaTypeSelector';
import { KnowledgeBaseContentType, CONTENT_TYPE_LABELS } from '../../types/knowledge-base';
import {
  RiArrowGoBackLine as UndoIcon,
  RiArrowGoForwardLine as RedoIcon,
  RiH1 as H1Icon,
  RiH2 as H2Icon,
  RiH3 as H3Icon,
  RiBold as BoldIcon,
  RiItalic as ItalicIcon,
  RiStrikethrough as StrikeIcon,
  RiMarkPenLine as HighlightIcon,
  RiAlignLeft as AlignLeftIcon,
  RiAlignCenter as AlignCenterIcon,
  RiAlignRight as AlignRightIcon,
  RiAlignJustify as AlignJustifyIcon,
  RiListOrdered as OrderedListIcon,
  RiListUnordered as BulletListIcon,
} from 'react-icons/ri';
import clsx from 'clsx';

interface DocumentEditorProps {
  document: KnowledgeBaseDocument | null;
  defaultFolderId?: string;
  onClose: () => void;
  onSaved: () => void;
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  document,
  defaultFolderId,
  onClose,
  onSaved,
}) => {
  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<KnowledgeBaseContentType>(
    KnowledgeBaseContentType.TEMPLATE
  );
  const [tags, setTags] = useState<string[]>([]);
  const [visaTypes, setVisaTypes] = useState<string[]>([]);
  const [folderId, setFolderId] = useState<string | undefined>(defaultFolderId);
  const [loading, setLoading] = useState(false);
  const [autoLabeling, setAutoLabeling] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
      }),
      Highlight,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Markdown,
    ],
    content: document?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  useEffect(() => {
    if (document) {
      setTitle(document.title);
      setContentType(document.contentType as KnowledgeBaseContentType);
      setTags(document.tags);
      setVisaTypes(document.visaTypes);
      setFolderId(document.folderId || undefined);

      if (editor && document.content) {
        editor.commands.setContent(document.content);
      }
    }
  }, [document, editor]);

  const handleAutoLabel = async () => {
    if (!editor) {
      alert('Editor not initialized');
      return;
    }

    const content = editor.getText();
    if (!content) {
      alert('Please enter content first');
      return;
    }

    try {
      setAutoLabeling(true);
      const result = await knowledgeBaseAIApi.autoLabel({
        content,
        title,
        contentType,
      });

      // Apply suggested labels with high confidence
      const highConfidenceTags = result.suggestedTags
        .filter((t) => t.confidence > 0.6)
        .map((t) => t.tag);
      const highConfidenceVisaTypes = result.suggestedVisaTypes
        .filter((v) => v.confidence > 0.6)
        .map((v) => v.visaType);

      setTags([...new Set([...tags, ...highConfidenceTags])]);
      setVisaTypes([...new Set([...visaTypes, ...highConfidenceVisaTypes])]);

      if (result.suggestedContentType.confidence > 0.7) {
        setContentType(result.suggestedContentType.type as KnowledgeBaseContentType);
      }

      alert('Auto-labeling complete! Review and adjust the suggestions as needed.');
    } catch (error) {
      console.error('Auto-labeling failed:', error);
      alert('Auto-labeling failed. Please try again.');
    } finally {
      setAutoLabeling(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!editor) {
      alert('Editor not initialized');
      return;
    }

    const content = editor.getText();
    if (!content.trim()) {
      alert('Please enter content');
      return;
    }

    try {
      setLoading(true);

      const data = {
        title,
        content,
        contentType,
        tags,
        visaTypes,
        folderId: folderId || null,
      };

      if (document) {
        await knowledgeBaseDocumentApi.update(document.id, {
          ...data,
          folderId: folderId || undefined,
        });
      } else {
        await knowledgeBaseDocumentApi.create({
          ...data,
          folderId: folderId || undefined,
        });
      }

      onSaved();
    } catch (error) {
      console.error('Failed to save document:', error);
      alert('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const isActive = useCallback(
    (name: string, attrs: Record<string, any> = {}) =>
      !!editor?.isActive(name as any, attrs),
    [editor]
  );

  const run = useCallback(
    (fn: (e: typeof editor) => void) => () => {
      if (!editor) return;
      fn(editor);
    },
    [editor]
  );

  const toolbarButton = useCallback(
    ({
      onClick,
      active,
      label,
      icon,
    }: {
      onClick: () => void;
      active?: boolean;
      label: string;
      icon: React.ReactNode;
    }) => (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'flex items-center justify-center rounded px-2 py-1 text-neutral-700 hover:bg-neutral-200 transition-colors',
          active && 'bg-neutral-200 text-neutral-800 border border-neutral-300'
        )}
        aria-label={label}
        title={label}
      >
        {icon}
      </button>
    ),
    []
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {document ? 'Edit Document' : 'Create Document'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Artificial Intelligence - Field Definition"
            />
          </div>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value as KnowledgeBaseContentType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.values(KnowledgeBaseContentType).map((type) => (
                <option key={type} value={type}>
                  {CONTENT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          {/* Content Editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Content</label>
              <button
                onClick={handleAutoLabel}
                disabled={autoLabeling}
                className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
              >
                <SparklesIcon className="w-4 h-4 mr-1" />
                {autoLabeling ? 'Auto-labeling...' : 'AI Labeling'}
              </button>
            </div>

            {/* Editor Toolbar */}
            <div className="bg-gray-50 border border-gray-300 rounded-t-md px-2 py-2 flex flex-wrap items-center gap-1">
              {toolbarButton({
                onClick: run(e => e.chain().focus().undo().run()),
                label: 'Undo',
                icon: <UndoIcon className="w-4 h-4" />,
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().redo().run()),
                label: 'Redo',
                icon: <RedoIcon className="w-4 h-4" />,
              })}

              <div className="block w-px h-5 bg-gray-400 mx-1" />

              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleHeading({ level: 1 }).run()),
                label: 'H1',
                icon: <H1Icon className="w-4 h-4" />,
                active: isActive('heading', { level: 1 }),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleHeading({ level: 2 }).run()),
                label: 'H2',
                icon: <H2Icon className="w-4 h-4" />,
                active: isActive('heading', { level: 2 }),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleHeading({ level: 3 }).run()),
                label: 'H3',
                icon: <H3Icon className="w-4 h-4" />,
                active: isActive('heading', { level: 3 }),
              })}

              <div className="block w-px h-5 bg-gray-400 mx-1" />

              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleBold().run()),
                label: 'Bold',
                icon: <BoldIcon className="w-4 h-4" />,
                active: isActive('bold'),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleItalic().run()),
                label: 'Italic',
                icon: <ItalicIcon className="w-4 h-4" />,
                active: isActive('italic'),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleStrike().run()),
                label: 'Strike',
                icon: <StrikeIcon className="w-4 h-4" />,
                active: isActive('strike'),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleHighlight().run()),
                label: 'Highlight',
                icon: <HighlightIcon className="w-4 h-4" />,
                active: isActive('highlight'),
              })}

              <div className="block w-px h-5 bg-gray-400 mx-1" />

              {toolbarButton({
                onClick: run(e => e.chain().focus().setTextAlign('left').run()),
                label: 'Align left',
                icon: <AlignLeftIcon className="w-4 h-4" />,
                active: isActive({ textAlign: 'left' } as any),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().setTextAlign('center').run()),
                label: 'Align center',
                icon: <AlignCenterIcon className="w-4 h-4" />,
                active: isActive({ textAlign: 'center' } as any),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().setTextAlign('right').run()),
                label: 'Align right',
                icon: <AlignRightIcon className="w-4 h-4" />,
                active: isActive({ textAlign: 'right' } as any),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().setTextAlign('justify').run()),
                label: 'Align justify',
                icon: <AlignJustifyIcon className="w-4 h-4" />,
                active: isActive({ textAlign: 'justify' } as any),
              })}

              <div className="block w-px h-5 bg-gray-400 mx-1" />

              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleBulletList().run()),
                label: 'Bullet list',
                icon: <BulletListIcon className="w-4 h-4" />,
                active: isActive('bulletList'),
              })}
              {toolbarButton({
                onClick: run(e => e.chain().focus().toggleOrderedList().run()),
                label: 'Ordered list',
                icon: <OrderedListIcon className="w-4 h-4" />,
                active: isActive('orderedList'),
              })}
            </div>

            {/* TipTap Editor */}
            <div className="w-full border border-t-0 border-gray-300 rounded-b-md bg-white overflow-y-auto" style={{ maxHeight: '400px' }}>
              <EditorContent editor={editor} />
            </div>

            <p className="mt-1 text-xs text-gray-500">
              Rich text editor with Markdown support. Use the toolbar for formatting.
            </p>
          </div>

          {/* Visa Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visa Types</label>
            <VisaTypeSelector value={visaTypes} onChange={setVisaTypes} />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TagInput value={tags} onChange={setTags} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Document'}
          </button>
        </div>
      </div>
    </div>
  );
};
